/* eslint-disable react/prop-types */
import {
  AccountAuthMethodSettings,
  CustomAuthMethodInput,
  CustomAuthMethodTypeEnum,
  GetUsersQuery,
} from '@pypestream/api-services/urql';
import {
  Button,
  Card,
  EntityTypes,
  Filepond,
  GridCell,
  Icon,
  IconButton,
  Input,
  InputProps,
  Select,
  SelectOption,
  Stack,
  Textarea,
  TextCaption,
  TextSubtitle,
  Tooltip,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { FC, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { useController, UseFormReturn, useWatch } from 'react-hook-form';
import { useManagerCtxSelector } from '../../../xstate/app.xstate';
import { FormOrgInfoType } from './create-organization';
import { CommonConfirmationModal } from '../../../components/modals/common-confirmation-modal/common-confirmation-modal';
import { NAME_ID_POLICY_FORMAT_VALUES } from './constants';

export type ReturnFormType = UseFormReturn<FormOrgInfoType, unknown, undefined>;

export type CustomAuthMethodSettingsType = NonNullable<
  NonNullable<CustomAuthMethodInput>
> & {
  isActive: boolean;
  name: string;
  isDefault?: boolean;
  accountAuthMethodSettingId?: string;
};

export type DefaultAuthMethodSettingsType = AccountAuthMethodSettings & {
  name: string;
  isDefault?: boolean;
  accountAuthMethodSettingId?: string;
};

export type Users = NonNullable<
  NonNullable<GetUsersQuery['admin_']>['users']
>['rows'];

export type MethodCollapseState = {
  [key: string]: boolean | undefined;
};

export type MethodDeactivateModalState = {
  [key: string]: boolean | undefined;
};

export class CreateOrganizationFields {
  public OrganizationNameField = forwardRef(
    (
      props: InputProps & {
        _form: ReturnFormType;
        autosave?: boolean;
      },
      ref: ForwardedRef<InputProps>
    ) => {
      const { t } = useTranslation();

      const { field: nameField, fieldState: nameFiledState } = useController({
        name: 'name',
        control: props._form.control || undefined,
        rules: { required: 'This is a required field.' },
      });

      const field = (
        <Input
          test-id="org-name-input"
          placeholder={`${t('manager/organizations:createOrg.placeholders.name')}`}
          variant="outlined"
          type="text"
          autocomplete="off"
          value={nameField.value}
          helpText={nameFiledState.error?.message}
          hasError={nameFiledState.invalid}
          name={nameField.name}
          readonly={props.readonly}
          onInput={nameField.onChange}
        />
      );

      return field;
    }
  );

  public OrgLoginMethodsField = forwardRef(
    (
      props: InputProps & {
        _form: ReturnFormType;
        autosave?: boolean;
        isEdit?: boolean;
        customAuthMethods?: CustomAuthMethodInput[];
      },
      ref: ForwardedRef<InputProps>
    ) => {
      const { t } = useTranslation();
      const [methodCollapseState, setMethodCollapseState] =
        useState<MethodCollapseState>({});
      const [methodDeactivateModalState, setMethodDeactivateModalState] =
        useState<MethodDeactivateModalState>({});

      const authMethodRules = {
        required: 'At least one login method must be active',
        validate: async (
          value: DefaultAuthMethodSettingsType[] | undefined
        ) => {
          const customAuthMethods = props._form.getValues('customAuthMethods');
          const defaultAuthMethods =
            props._form.getValues('defaultAuthMethods');
          const enabledAuthMethods = [
            ...(defaultAuthMethods || []),
            ...(customAuthMethods || []),
          ].some((authMethod) => authMethod.isActive);

          if (!enabledAuthMethods) {
            return 'At least one login method must be active';
          }

          return !!enabledAuthMethods;
        },
      };

      const {
        field: defaultAuthMethodsField,
        fieldState: defaultAuthMethodsFieldState,
      } = useController({
        name: 'defaultAuthMethods',
        control: props._form.control || undefined,
        rules: authMethodRules,
      });

      const customAuthMethodsFields = useWatch({
        name: 'customAuthMethods',
        control: props._form.control,
      }) as CustomAuthMethodSettingsType[];

      useEffect(() => {
        if (!props.isEdit) return;

        customAuthMethodsFields.forEach((method) => {
          setMethodCollapseState((prevState) => {
            if (prevState[method.type!] !== undefined) return prevState;
            return {
              ...prevState,
              [method.type!]: true,
            };
          });
        });
      }, [props.isEdit, customAuthMethodsFields]);

      const defaultEnabledAuthMethods = (
        defaultAuthMethodsField.value || []
      )?.filter((defaultAuthMethod) => defaultAuthMethod.isActive);

      const customEnabledAuthMethod = customAuthMethodsFields.filter(
        (customAuthMethod) => customAuthMethod.isActive
      );

      const enabledAuthMethods = [
        ...defaultEnabledAuthMethods,
        ...customEnabledAuthMethod,
      ];

      const defaultAuthMethods = defaultAuthMethodsField.value?.map(
        (defaultAuthMethod, defaultAuthMethodIndex) => {
          const isOnlyAuthMethodActive = enabledAuthMethods.length === 1;
          // @todo - use providerId instead of name
          const isPasswordAuthMethod =
            defaultAuthMethod.name === 'Email & Password';
          return (
            <GridCell key={`${defaultAuthMethod.id}-${defaultAuthMethod.name}`}>
              <Card
                size="small"
                border="default"
                shadow="none"
                style={{ width: '100%' }}
              >
                <Stack
                  alignItems="center"
                  gutter="xsmall"
                  style={{ width: '100%' }}
                >
                  <Stack
                    alignItems="center"
                    gutter="xsmall"
                    justifyContent="space-between"
                    style={{ width: '100%' }}
                  >
                    <Stack alignItems="center" gutter="xsmall">
                      <Icon
                        name={isPasswordAuthMethod ? 'email-open' : 'google'}
                        size="small"
                        {...(isPasswordAuthMethod && {
                          style: {
                            fill: 'white',
                          },
                        })}
                      ></Icon>
                      <TextSubtitle size="small" variant="secondary">
                        {defaultAuthMethod.name}
                      </TextSubtitle>
                    </Stack>
                    <Stack>
                      <Button
                        test-id={`${defaultAuthMethod.name}-auth-method-btn`}
                        size="small"
                        variant={
                          defaultAuthMethod.isActive ? 'danger' : 'primary'
                        }
                        onClick={() => {
                          if (defaultAuthMethod.isActive) {
                            setMethodDeactivateModalState({
                              [defaultAuthMethod.name]: true,
                            });
                          } else if (
                            defaultAuthMethodsField.value &&
                            defaultAuthMethodsField.value.length >
                              defaultAuthMethodIndex
                          ) {
                            defaultAuthMethodsField.value[
                              defaultAuthMethodIndex
                            ].isActive =
                              !defaultAuthMethodsField.value[
                                defaultAuthMethodIndex
                              ].isActive;
                            defaultAuthMethodsField.onChange(
                              defaultAuthMethodsField.value
                            );
                          }
                        }}
                        disabled={
                          isOnlyAuthMethodActive && defaultAuthMethod.isActive
                        }
                      >
                        {isOnlyAuthMethodActive &&
                        defaultAuthMethod.isActive ? (
                          <Tooltip content="To deactivate this login method, first activate another one.">
                            <span>Deactivate</span>
                          </Tooltip>
                        ) : defaultAuthMethod.isActive ? (
                          'Deactivate'
                        ) : (
                          'Activate'
                        )}
                      </Button>
                      <CommonConfirmationModal
                        headerText={`Deactivate ${defaultAuthMethod.name}?`}
                        bodyText={`Once deactivated, users in this Org will no longer be able to login with ${defaultAuthMethod.name}.`}
                        headerTextAlign="center"
                        bodyTextAlign="center"
                        confirmText="Deactivate"
                        isModalOpened={
                          methodDeactivateModalState[defaultAuthMethod.name]
                        }
                        onClose={() => {
                          setMethodDeactivateModalState({
                            [defaultAuthMethod.name]: false,
                          });
                        }}
                        onConfirm={() => {
                          if (
                            defaultAuthMethodsField.value &&
                            defaultAuthMethodsField.value.length >
                              defaultAuthMethodIndex
                          ) {
                            defaultAuthMethodsField.value[
                              defaultAuthMethodIndex
                            ].isActive =
                              !defaultAuthMethodsField.value[
                                defaultAuthMethodIndex
                              ].isActive;
                            defaultAuthMethodsField.onChange(
                              defaultAuthMethodsField.value
                            );
                          }
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Card>
            </GridCell>
          );
        }
      );

      const customAuthMethods = customAuthMethodsFields.map(
        (customAuthMethod, customAuthMethodIndex) => {
          const isOnlyAuthMethodActive = enabledAuthMethods.length === 1;
          const isSamlAuthMethod =
            customAuthMethod.type === CustomAuthMethodTypeEnum.Saml;

          const clientIdField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.clientId`
          );

          const clientSecretField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.clientSecret`
          );

          const issuerURLField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.issuerURL`
          );

          const usernameAttributeField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.usernameAttribute`
          );

          const emailAttributeField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.emailAttribute`
          );

          const entityIssuerField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.entityIssuer`
          );

          const nameIDPolicyFormatField = props._form.getFieldState(
            `customAuthMethods.${customAuthMethodIndex}.settings.nameIDPolicyFormat`
          );

          const ClientSecretInput = isSamlAuthMethod ? Textarea : Input;

          const methodTitle = `Custom SSO via ${customAuthMethod.type === CustomAuthMethodTypeEnum.Oidc ? 'OpenID Connect' : 'SAML'}`;

          const { isActive } = customAuthMethodsFields[customAuthMethodIndex];

          return (
            <GridCell key={`${customAuthMethod.id}-${customAuthMethod.name}`}>
              <Card
                size="small"
                border="default"
                shadow="none"
                style={{ width: '100%' }}
                test-id={customAuthMethod.type}
              >
                <Stack
                  alignItems="center"
                  gutter="xsmall"
                  style={{ width: '100%' }}
                >
                  <Stack
                    alignItems="center"
                    gutter="xsmall"
                    justifyContent="space-between"
                    style={{ width: '100%' }}
                  >
                    <Stack alignItems="center" gutter="xsmall">
                      {isActive && props.isEdit && (
                        <IconButton
                          name={
                            methodCollapseState[customAuthMethod.type!]
                              ? 'chevron-down'
                              : 'chevron-up'
                          }
                          size="xsmall"
                          onClick={() => {
                            setMethodCollapseState({
                              ...methodCollapseState,
                              [customAuthMethod.type!]:
                                !methodCollapseState[customAuthMethod.type!],
                            });
                          }}
                        ></IconButton>
                      )}
                      <Icon name={'lock'} size="small"></Icon>
                      <TextSubtitle size="small" variant="secondary">
                        {methodTitle}
                      </TextSubtitle>
                    </Stack>
                    <Stack>
                      <Button
                        test-id={`${methodTitle}-auth-method-btn`}
                        size="small"
                        variant={isActive ? 'danger' : 'primary'}
                        onClick={() => {
                          if (isActive) {
                            setMethodDeactivateModalState({
                              [customAuthMethod.type!]: true,
                            });
                          } else {
                            setMethodCollapseState({
                              ...methodCollapseState,
                              [customAuthMethod.type!]: isActive,
                            });
                            props._form.setValue(
                              `customAuthMethods.${customAuthMethodIndex}.isActive`,
                              !isActive,
                              { shouldDirty: true }
                            );
                          }
                        }}
                        disabled={isOnlyAuthMethodActive && isActive}
                      >
                        {isOnlyAuthMethodActive && isActive ? (
                          <Tooltip content="To deactivate this login method, first activate another one.">
                            <span>Deactivate</span>
                          </Tooltip>
                        ) : isActive ? (
                          'Deactivate'
                        ) : (
                          'Activate'
                        )}
                      </Button>
                      <CommonConfirmationModal
                        headerText={`Deactivate ${methodTitle}?`}
                        bodyText={`Once deactivated, users in this Org will no longer be able to login with ${methodTitle}.`}
                        headerTextAlign="center"
                        bodyTextAlign="center"
                        confirmText="Deactivate"
                        isModalOpened={
                          methodDeactivateModalState[customAuthMethod.type!]
                        }
                        onClose={() => {
                          setMethodDeactivateModalState({
                            [customAuthMethod.type!]: false,
                          });
                        }}
                        onConfirm={() => {
                          setMethodCollapseState({
                            ...methodCollapseState,
                            [customAuthMethod.type!]: isActive,
                          });
                          props._form.resetField(
                            `customAuthMethods.${customAuthMethodIndex}`
                          );
                          props._form.setValue(
                            `customAuthMethods.${customAuthMethodIndex}.isActive`,
                            !isActive,
                            { shouldDirty: true }
                          );
                        }}
                      />
                    </Stack>
                    {isActive &&
                      !methodCollapseState[customAuthMethod.type!] && (
                        <div style={{ width: '100%' }}>
                          <GridCell xsmall="12" medium="12">
                            <TextSubtitle size="small" variant="secondary">
                              {customAuthMethod.type ===
                              CustomAuthMethodTypeEnum.Oidc ? (
                                <TranslationComponent i18nKey="manager/organizations:createOrg.clientId">
                                  Client ID*
                                </TranslationComponent>
                              ) : (
                                <TranslationComponent i18nKey="manager/organizations:createOrg.clientIdSaml">
                                  Single Sign-On URL*
                                </TranslationComponent>
                              )}
                            </TextSubtitle>
                            <Input
                              placeholder={
                                (customAuthMethod.type ===
                                CustomAuthMethodTypeEnum.Oidc
                                  ? t(
                                      'manager/organizations:createOrg.clientId'
                                    )
                                  : t(
                                      'manager/organizations:createOrg.clientIdSaml'
                                    )) || ''
                              }
                              variant="outlined"
                              type="text"
                              autocomplete="off"
                              readonly={props.readonly}
                              disabled={props.disabled || !isActive}
                              hasError={clientIdField.invalid}
                              helpText={clientIdField.error?.message}
                              test-id={
                                customAuthMethod.type ===
                                CustomAuthMethodTypeEnum.Oidc
                                  ? 'client-id-input'
                                  : 'sso-url-input'
                              }
                              {...props._form.register(
                                `customAuthMethods.${customAuthMethodIndex}.clientId`,
                                {
                                  required:
                                    t(
                                      'manager/organizations:createOrg.fieldIsRequired',
                                      {
                                        field:
                                          customAuthMethod.type ===
                                          CustomAuthMethodTypeEnum.Oidc
                                            ? t(
                                                'manager/organizations:createOrg.clientId'
                                              )
                                            : t(
                                                'manager/organizations:createOrg.clientIdSaml'
                                              ),
                                      }
                                    ) || '',
                                  disabled: !isActive,
                                }
                              )}
                            />
                          </GridCell>
                          <GridCell xsmall="12" medium="12">
                            <TextSubtitle size="small" variant="secondary">
                              {customAuthMethod.type ===
                              CustomAuthMethodTypeEnum.Oidc ? (
                                <TranslationComponent i18nKey="manager/organizations:createOrg.clientSecret">
                                  Client Secret*
                                </TranslationComponent>
                              ) : (
                                <TranslationComponent i18nKey="manager/organizations:createOrg.clientSecretSaml">
                                  X.509 Certificate*
                                </TranslationComponent>
                              )}
                            </TextSubtitle>
                            <ClientSecretInput
                              placeholder={
                                (customAuthMethod.type ===
                                CustomAuthMethodTypeEnum.Oidc
                                  ? t(
                                      'manager/organizations:createOrg.clientSecret'
                                    )
                                  : t(
                                      'manager/organizations:createOrg.clientSecretSaml'
                                    )) || ''
                              }
                              variant="outlined"
                              {...((isSamlAuthMethod && {
                                rows: 4,
                              }) || {
                                type: 'password',
                              })}
                              autocomplete="off"
                              disabled={props.disabled || !isActive}
                              hasError={clientSecretField.invalid}
                              helpText={clientSecretField.error?.message}
                              test-id={
                                customAuthMethod.type ===
                                CustomAuthMethodTypeEnum.Oidc
                                  ? 'client-secret-input'
                                  : 'client-certificate-input'
                              }
                              {...props._form.register(
                                `customAuthMethods.${customAuthMethodIndex}.clientSecret`,
                                {
                                  required:
                                    t(
                                      'manager/organizations:createOrg.fieldIsRequired',
                                      {
                                        field:
                                          customAuthMethod.type ===
                                          CustomAuthMethodTypeEnum.Oidc
                                            ? t(
                                                'manager/organizations:createOrg.clientSecret'
                                              )
                                            : t(
                                                'manager/organizations:createOrg.clientSecretSaml'
                                              ),
                                      }
                                    ) || '',
                                  disabled: !isActive,
                                }
                              )}
                            />
                          </GridCell>
                          <GridCell xsmall="12" medium="12">
                            <TextSubtitle size="small" variant="secondary">
                              {customAuthMethod.type ===
                              CustomAuthMethodTypeEnum.Oidc ? (
                                <TranslationComponent i18nKey="manager/organizations:createOrg.issuerURL">
                                  Issuer URI*
                                </TranslationComponent>
                              ) : (
                                <TranslationComponent i18nKey="manager/organizations:createOrg.issuerURLSaml">
                                  Service Provider Entity ID*
                                </TranslationComponent>
                              )}
                            </TextSubtitle>
                            <Input
                              placeholder={
                                (customAuthMethod.type ===
                                CustomAuthMethodTypeEnum.Oidc
                                  ? t(
                                      'manager/organizations:createOrg.issuerURL'
                                    )
                                  : t(
                                      'manager/organizations:createOrg.issuerURLSaml'
                                    )) || ''
                              }
                              variant="outlined"
                              type="text"
                              autocomplete="off"
                              disabled={props.disabled || !isActive}
                              hasError={issuerURLField.invalid}
                              helpText={issuerURLField.error?.message}
                              test-id={
                                customAuthMethod.type ===
                                CustomAuthMethodTypeEnum.Oidc
                                  ? 'issuer-uri-input'
                                  : 'service-provider-entity-id-input'
                              }
                              {...props._form.register(
                                `customAuthMethods.${customAuthMethodIndex}.issuerURL`,
                                {
                                  required:
                                    t(
                                      'manager/organizations:createOrg.fieldIsRequired',
                                      {
                                        field:
                                          customAuthMethod.type ===
                                          CustomAuthMethodTypeEnum.Oidc
                                            ? t(
                                                'manager/organizations:createOrg.issuerURL'
                                              )
                                            : t(
                                                'manager/organizations:createOrg.issuerURLSaml'
                                              ),
                                      }
                                    ) || '',
                                  validate: async (
                                    value: string | undefined
                                  ) => {
                                    if (!value) return true;
                                    try {
                                      const urlRegex =
                                        /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+[^\s]*$/i;
                                      const isValidUrl = urlRegex.test(value);

                                      if (!isValidUrl) {
                                        return 'Invalid Issuer URL';
                                      }

                                      return isValidUrl;
                                    } catch (err) {
                                      return 'Invalid Issuer URL';
                                    }
                                  },
                                  disabled: !isActive,
                                }
                              )}
                            />
                          </GridCell>

                          {(isSamlAuthMethod && (
                            <>
                              <GridCell xsmall="12" medium="12">
                                <TextSubtitle size="small" variant="secondary">
                                  <TranslationComponent i18nKey="manager/organizations:createOrg.usernameAttribute">
                                    Username Attribute Name*
                                  </TranslationComponent>
                                </TextSubtitle>
                                <Input
                                  placeholder={`${t('manager/organizations:createOrg.usernameAttribute')}`}
                                  variant="outlined"
                                  type="text"
                                  autocomplete="off"
                                  disabled={props.disabled || !isActive}
                                  hasError={usernameAttributeField.invalid}
                                  helpText={
                                    usernameAttributeField.error?.message
                                  }
                                  test-id="username-attribute-input"
                                  {...props._form.register(
                                    `customAuthMethods.${customAuthMethodIndex}.usernameAttribute`,
                                    {
                                      required:
                                        t(
                                          'manager/organizations:createOrg.fieldIsRequired',
                                          {
                                            field: t(
                                              'manager/organizations:createOrg.usernameAttribute'
                                            ),
                                          }
                                        ) || '',
                                      disabled: !isActive,
                                    }
                                  )}
                                />
                              </GridCell>

                              <GridCell xsmall="12" medium="12">
                                <TextSubtitle size="small" variant="secondary">
                                  <TranslationComponent i18nKey="manager/organizations:createOrg.emailAttribute">
                                    Email Attribute Name*
                                  </TranslationComponent>
                                </TextSubtitle>
                                <Input
                                  placeholder={`${t('manager/organizations:createOrg.emailAttribute')}`}
                                  variant="outlined"
                                  type="text"
                                  autocomplete="off"
                                  disabled={props.disabled || !isActive}
                                  hasError={emailAttributeField.invalid}
                                  helpText={emailAttributeField.error?.message}
                                  test-id="email-attribute-input"
                                  {...props._form.register(
                                    `customAuthMethods.${customAuthMethodIndex}.emailAttribute`,
                                    {
                                      required:
                                        t(
                                          'manager/organizations:createOrg.fieldIsRequired',
                                          {
                                            field: t(
                                              'manager/organizations:createOrg.emailAttribute'
                                            ),
                                          }
                                        ) || '',
                                      disabled: !isActive,
                                    }
                                  )}
                                />
                              </GridCell>

                              <GridCell xsmall="12" medium="12">
                                <TextSubtitle size="small" variant="secondary">
                                  <TranslationComponent i18nKey="manager/organizations:createOrg.entityIssuer">
                                    Identity Provider Entity ID (Issuer)
                                  </TranslationComponent>
                                </TextSubtitle>
                                <Input
                                  placeholder={`${t('manager/organizations:createOrg.entityIssuer')}`}
                                  variant="outlined"
                                  type="text"
                                  autocomplete="off"
                                  disabled={props.disabled || !isActive}
                                  hasError={entityIssuerField.invalid}
                                  helpText={
                                    t(
                                      'manager/organizations:createOrg.entityIssuerHelpText'
                                    ) || ''
                                  }
                                  test-id="identity-provider-entity-id-input"
                                  {...props._form.register(
                                    `customAuthMethods.${customAuthMethodIndex}.entityIssuer`,
                                    {
                                      disabled: !isActive,
                                    }
                                  )}
                                />
                              </GridCell>

                              <GridCell xsmall="12" medium="12">
                                <TextSubtitle size="small" variant="secondary">
                                  <TranslationComponent i18nKey="manager/organizations:createOrg.nameIDPolicyFormat">
                                    Name ID Policy Format
                                  </TranslationComponent>
                                </TextSubtitle>
                                <Select
                                  placeholder={`${t('manager/organizations:createOrg.nameIDPolicyFormat')}`}
                                  variant="outlined"
                                  disabled={
                                    props.disabled || !customAuthMethod.isActive
                                  }
                                  test-id="name-id-policy-format-input"
                                  clearable
                                  helpText={
                                    t(
                                      'manager/organizations:createOrg.nameIDPolicyFormatHelpText'
                                    ) || ''
                                  }
                                  placement="top"
                                  {...props._form.register(
                                    `customAuthMethods.${customAuthMethodIndex}.settings.nameIDPolicyFormat`,
                                    {
                                      disabled: !customAuthMethod.isActive,
                                    }
                                  )}
                                >
                                  {NAME_ID_POLICY_FORMAT_VALUES.map(
                                    (nameIDPolicyFormatValue) => (
                                      <SelectOption
                                        key={nameIDPolicyFormatValue}
                                        value={nameIDPolicyFormatValue}
                                      >
                                        {nameIDPolicyFormatValue}
                                      </SelectOption>
                                    )
                                  )}
                                </Select>
                              </GridCell>
                            </>
                          )) || (
                            <>
                              <GridCell xsmall="12" medium="12">
                                <TextSubtitle size="small" variant="secondary">
                                  <TranslationComponent i18nKey="manager/organizations:createOrg.redirectURL">
                                    Redirect URI*
                                  </TranslationComponent>
                                </TextSubtitle>
                                <Input
                                  placeholder={`${t('manager/organizations:createOrg.redirectURL')}`}
                                  variant="outlined"
                                  type="text"
                                  autocomplete="off"
                                  value={
                                    props.isEdit && customAuthMethod.id
                                      ? `${import.meta.env.FE_KRATOS_ENDPOINT}/self-service/methods/oidc/callback/${customAuthMethod.id}`
                                      : `${props.isEdit ? 'Update' : 'Create'} Org to show redirect URI`
                                  }
                                  disabled
                                  readonly
                                  helpText={
                                    t(
                                      'manager/organizations:createOrg.redirectURLHelpText'
                                    ) || ''
                                  }
                                >
                                  {!!(props.isEdit && customAuthMethod.id) && (
                                    <IconButton
                                      name="copy"
                                      slot="suffix"
                                      size="small"
                                      variant="tertiary"
                                      style={{ marginRight: '-4px' }}
                                      onclick={() =>
                                        navigator.clipboard.writeText(
                                          `${import.meta.env.FE_KRATOS_ENDPOINT}/self-service/methods/oidc/callback/${customAuthMethod.id}`
                                        )
                                      }
                                    />
                                  )}
                                </Input>
                              </GridCell>
                            </>
                          )}
                        </div>
                      )}
                  </Stack>
                </Stack>
              </Card>
            </GridCell>
          );
        }
      );

      return (
        <>
          {defaultAuthMethods}
          {customAuthMethods}
          {defaultAuthMethodsFieldState.error?.message && (
            <span data-testid="auth-method-error-message">
              <TextCaption variant="danger">
                {defaultAuthMethodsFieldState.error?.message}
              </TextCaption>
            </span>
          )}
        </>
      );
    }
  );

  public OrganizationIconField: FC<{
    form: ReturnFormType;
    readOnly: boolean;
  }> = ({ form, readOnly }) => {
    /* eslint-disable react-hooks/rules-of-hooks */
    const { t } = useTranslation();

    const { field: orgIconField } = useController({
      name: 'picture',
      control: form.control || undefined,
    });

    const { field: orgIconIdField } = useController({
      name: 'accountIconId',
      control: form.control || undefined,
    });

    const accountId = useManagerCtxSelector((ctx) => ctx.selectedOrgId);
    /* eslint-enable react-hooks/rules-of-hooks */
    return (
      <Filepond
        test-id="org-icon-input"
        readonly={readOnly}
        entityType={EntityTypes.ACCOUNT}
        accountId={accountId}
        previewLabel={
          t('manager/organizations:createOrg.placeholders.dragPicture', {
            defaultValue: 'Drag an photo here',
          }) || ''
        }
        previewCta={
          t('manager/organizations:createOrg.placeholders.selectFile', {
            defaultValue: 'Select a file',
          }) || ''
        }
        pictureSrc={orgIconField.value}
        name={orgIconField.name}
        onChange={({ url, pictureId }) => {
          orgIconField.onChange(url || '');
          orgIconIdField.onChange(pictureId || '');
        }}
      />
    );
  };

  public OrganizationAccountManagerField: FC<{
    users: Users;
  }> = ({ users, ...props }) => {
    // @todo - fix this as we should not be using hooks in a class component
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { t } = useTranslation();
    return (
      <Select
        variant="outlined"
        placeholder={`${t('manager/organizations:createOrg.placeholders.accountManager')}`}
        clearable
        {...props}
      >
        {(users || []).map((accountManager) => (
          <SelectOption key={accountManager.id} value={accountManager.id}>
            {`${accountManager.firstName} ${accountManager.lastName} (${accountManager.userName})`}
          </SelectOption>
        ))}
      </Select>
    );
  };
}
